/**
 * ExpelSkillEffect — Poison tree Expel path toggle active (Fork 1).
 *
 * A persistent poison aura centered on the player. While active, a 1.5-tile
 * radius zone follows the player, applying continuous `poison` DoT to every
 * enemy within range. The aura drains `toxinLoad` — a stamina-like resource
 * pool of 100 units — at 6.67/sec, giving ~15 seconds of continuous uptime.
 * ToxinLoad recharges at 10/sec while Expel is off (~10 seconds to full).
 *
 * Break condition: any single incoming hit dealing more than 20% of the
 * player's max HP deactivates Expel immediately. Expel Mk.II (evolution)
 * upgrades this to tolerate one such hit before dropping on the second.
 *
 * Activation: toggle. First press activates (creates the aura zone), second
 * press deactivates manually. Auto-deactivates when toxinLoad reaches 0 or
 * HP break triggers. Medium cooldown applies on deactivation for reactivation.
 *
 * Upgrade hooks:
 * - Membrane (expel_radius): `auraRadiusBonus` — when player HP > 75%,
 *   radius increases by 25% (1.5 → 1.875 tiles). Checked per-frame.
 * - Venomblood (expel_retaliate): handled externally — not wired here.
 * - Footwork: applied through ZoneSystem's speed modifier on the zone.
 *
 * Extends ToggleSkillEffect for active/cooldown lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Expel (expel)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { ZoneConfig } from '../../../systems/ZoneSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ExpelDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
    remove(zoneId: string): void;
    getZone(zoneId: string): { currentRadiusTiles: number } | undefined;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly player: {
    getMaxHp(): number;
    getCurrentHpRatio(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ExpelConfig {
  /** Cooldown in ms after deactivation (medium tier = 1200ms). */
  readonly cooldownMs: number;
  /** Base aura radius in tiles. */
  readonly baseRadiusTiles: number;
  /** Poison DoT intensity applied per tick. */
  readonly dotIntensity: number;
  /** Poison DoT duration in ms per application. */
  readonly dotDurationMs: number;
  /** Total toxinLoad resource pool. */
  readonly toxinLoadMax: number;
  /** ToxinLoad drain per second while active. */
  readonly toxinDrainPerSecond: number;
  /** ToxinLoad recharge per second while inactive. */
  readonly toxinRechargePerSecond: number;
  /** HP ratio threshold — a hit dealing more than this fraction of maxHp breaks Expel. */
  readonly hpBreakThreshold: number;
  /** Number of above-threshold hits tolerated before break (0 = breaks immediately). */
  readonly breakTolerance: number;
}

const DEFAULT_CONFIG: ExpelConfig = {
  cooldownMs: 1200,              // medium cooldown tier
  baseRadiusTiles: 1.5,
  dotIntensity: 3,
  dotDurationMs: 4000,
  toxinLoadMax: 100,
  toxinDrainPerSecond: 6.67,     // ~15s uptime
  toxinRechargePerSecond: 10,    // ~10s to full
  hpBreakThreshold: 0.2,        // 20% maxHp
  breakTolerance: 0,             // base Expel breaks on first hit
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AURA_ZONE_TYPE = 'expel_aura';
const AURA_ZONE_ID_PREFIX = 'expel_aura_';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ExpelSkillEffect extends ToggleSkillEffect {
  readonly skillId: string = 'expel';

  private readonly deps: ExpelDeps;
  private readonly config: ExpelConfig;

  private _toxinLoad: number;
  private _activeZoneId: string | null = null;
  private _activationCount = 0;
  private _hitsAbsorbed = 0;

  constructor(deps: ExpelDeps, config: Partial<ExpelConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this._toxinLoad = this.config.toxinLoadMax;
  }

  // --- State getters ---

  get toxinLoad(): number { return this._toxinLoad; }
  get toxinLoadMax(): number { return this.config.toxinLoadMax; }
  get activeZoneId(): string | null { return this._activeZoneId; }
  get activationCount(): number { return this._activationCount; }
  get hitsAbsorbed(): number { return this._hitsAbsorbed; }

  // --- ToggleSkillEffect interface ---

  canActivate(): boolean {
    return !this._isActive && this.cooldownRemaining <= 0 && this._toxinLoad > 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._isActive = true;
    this._hitsAbsorbed = 0;

    const zoneId = `${AURA_ZONE_ID_PREFIX}${this._activationCount}`;

    this.deps.zoneSystem.create({
      id: zoneId,
      type: AURA_ZONE_TYPE,
      position: { x: 0, y: 0 }, // Will be updated immediately by followEntity
      durationMs: -1, // Permanent until manually removed
      sourceSkillId: 'expel',
      shape: 'circle',
      radiusTiles: this.resolveRadius(),
      dotDefId: 'poison',
      dotIntensity: this.config.dotIntensity,
      dotDurationMs: this.config.dotDurationMs,
      followEntity: 'player',
    });

    this._activeZoneId = zoneId;
    this._activationCount++;
    return true;
  }

  protected onDeactivate(): void {
    this.removeAuraZone();
    this.cooldownRemaining = this.config.cooldownMs;
  }

  protected updateWhileActive(delta: number): void {
    // Drain toxinLoad
    const drainAmount = this.config.toxinDrainPerSecond * (delta / 1000);
    this._toxinLoad = Math.max(0, this._toxinLoad - drainAmount);

    // Auto-deactivate if toxinLoad depleted
    if (this._toxinLoad <= 0) {
      this.deactivate();
      return;
    }

    // Update zone radius (Membrane upgrade: radius bonus when HP > 75%)
    this.updateZoneRadius();
  }

  override update(delta: number): void {
    super.update(delta);

    // Recharge toxinLoad while inactive
    if (!this._isActive && this._toxinLoad < this.config.toxinLoadMax) {
      const rechargeAmount = this.config.toxinRechargePerSecond * (delta / 1000);
      this._toxinLoad = Math.min(this.config.toxinLoadMax, this._toxinLoad + rechargeAmount);
    }
  }

  override destroy(): void {
    this.removeAuraZone();
    super.destroy();
  }

  // --- External event handler ---

  /**
   * Called by the game layer when the player takes damage. If the hit exceeds
   * the HP break threshold, either absorbs it (if tolerance remains) or
   * deactivates Expel.
   *
   * @param damageAmount Raw damage dealt to the player.
   * @returns true if Expel was deactivated by this hit.
   */
  handlePlayerHit(damageAmount: number): boolean {
    if (!this._isActive) return false;

    const threshold = this.config.hpBreakThreshold * this.deps.player.getMaxHp();
    if (damageAmount <= threshold) return false;

    // Hit exceeds threshold — check tolerance
    if (this._hitsAbsorbed < this.config.breakTolerance) {
      this._hitsAbsorbed++;
      return false;
    }

    // Tolerance exceeded — break
    this.deactivate();
    return true;
  }

  // --- Internal ---

  private resolveRadius(): number {
    let radius = this.config.baseRadiusTiles;

    // Membrane: +25% radius when HP > 75%
    if (this.deps.resolver.hasEffect('expel', 'auraRadiusBonus')) {
      const hpRatio = this.deps.player.getCurrentHpRatio();
      const bonus = this.deps.resolver.getAdditiveEffect('expel', 'auraRadiusBonus');
      const hpThreshold = 0.75; // Membrane's HP threshold from JSON
      if (hpRatio > hpThreshold) {
        radius *= (1 + bonus);
      }
    }

    return radius;
  }

  private updateZoneRadius(): void {
    if (this._activeZoneId === null) return;

    const zone = this.deps.zoneSystem.getZone(this._activeZoneId);
    if (!zone) return;

    zone.currentRadiusTiles = this.resolveRadius();
  }

  /**
   * Force-reactivate the aura without checking canActivate guards.
   * Used by Second Skin (ecdysis_upgrade_2): after Ecdysis consumes all
   * toxinLoad, this restores the pool and resets hit tolerance so the aura
   * continues seamlessly. If the aura is still active (zone not yet torn
   * down), only the resource state is refreshed. If somehow inactive,
   * creates a fresh zone.
   */
  forceReactivate(): void {
    // Restore full resource pool — "no cost" means a free cycle.
    this._toxinLoad = this.config.toxinLoadMax;

    // Fresh hit tolerance (spec: breakTolerance counter resets).
    this._hitsAbsorbed = 0;

    // Clear any pending cooldown.
    this.cooldownRemaining = 0;

    // If the aura zone was already torn down, stand it back up.
    if (!this._isActive) {
      this._isActive = true;
      const zoneId = `${AURA_ZONE_ID_PREFIX}${this._activationCount}`;

      this.deps.zoneSystem.create({
        id: zoneId,
        type: AURA_ZONE_TYPE,
        position: { x: 0, y: 0 },
        durationMs: -1,
        sourceSkillId: 'expel',
        shape: 'circle',
        radiusTiles: this.resolveRadius(),
        dotDefId: 'poison',
        dotIntensity: this.config.dotIntensity,
        dotDurationMs: this.config.dotDurationMs,
        followEntity: 'player',
      });

      this._activeZoneId = zoneId;
      this._activationCount++;
    }
  }

  private removeAuraZone(): void {
    if (this._activeZoneId !== null) {
      this.deps.zoneSystem.remove(this._activeZoneId);
      this._activeZoneId = null;
    }
  }
}
